/*
    Fetcher is a decorator function to add the Vue Fetcher component.
        * Arguments:
            - {Object} options
            - {Object} conf
        * Usage:
           The 'options' argument is the same configuration for a Vue Component. in addition, the next options:

           - name [string]: name used for Fetcher component in Vue. (by default is 'fetch'. Remember considering the prefix option.)

           in the 'conf' argument you can provide the next options:

           - fetchOnCreated [boolean]: by default is 'true', if the value is 'true' this will do a call for fetching from the start
           - fetchOnUpdateParams [boolean]: by default is 'true', if the value is 'true' this will do a call for fetching when the params are updated
           - fetchOnUpdatePostData [boolean]: by default is 'true', if the value is 'true' this will do a call for fetching when the 'post data' object is updated
           - url [string]: is the server URL that will be used for the request
           - method [string]: by default is 'GET', is the request method to be used when making the request
           - fetchOptions [object]: is the Axios config (https://github.com/axios/axios)
           - debounceWait [integer]: time to wait between every call to the fetch function, it uses for changes in fields for voiding multiple calls to the API when the user is typing ( time in milliseconds. by default: 500 milliseconds)

           Just Props:

           - url-prop [string]: replace the url option
           - method-prop [string]: replace the method option
           - fetch-on-created-prop [boolean]: replace the fetchOnCreated option
           - fetch-on-update-post-data-prop [boolean]: replace the fetchOnUpdatePostData option
           - fetch-on-update-params-prop [boolean]: replace the fetchOnUpdateParamsData option

           Methods:

           fetch(successFunc [function], options [Object]): you can call the fetch function for doing a call to the service or API. also you can pass the successFunc function for altering process by default when the response is successful, and the second parameter is for changing or modifying the Axios config (https://github.com/axios/axios)

           Others:

           Params ('params'): is an object with GET parameters for the request, you can use it as an object in the Vue Data (with v-model or as Vue variable).

           Post Data ('postData'): is an object with POST data parameters for the request, you can use it as an object in the Vue Data (with v-model or as Vue variable).

*/
import axios from 'axios';
import { debounce } from 'lib/misc';

const fetcher = ({name = 'fetch', props, data: dataProp, created, watch, methods, ...options}, conf = {}) => {
    const {
        fetchOnCreated = true,
        fetchOnUpdatePostData = true,
        fetchOnUpdateParams = true,
        url,
        method = 'GET',
        fetchOptions: _fetchOptions = {},
        fetchTrigger,
        debounceWait = 500
    } = conf;

    const { data: postData = {}, params = {}, fetchOptions } = _fetchOptions;

    return {
        name,
        props: {
            urlProp: String,
            methodProp: String,
            fetchOnCreatedProp: { type: Boolean, default: undefined },
            fetchOnUpdatePostDataProp: { type: Boolean, default: undefined },
            fetchOnUpdateParamsProp: { type: Boolean, default: undefined },
            ...props
        },
        data: function() {
            const _dataProp = dataProp && dataProp.call(this);
            let data = {
                url: this.urlProp || url,
                method: this.methodProp || method,
                fetchOptions,
                fetchOnCreated,
                fetchOnUpdateParams,
                fetchOnUpdatePostData,
                fetchTrigger: this.fetchTriggerProp || fetchTrigger,
                data: null,
                params,
                postData,
                ..._dataProp,
            };

            if (this.fetchOnCreatedProp !== undefined) {
                data.fetchOnCreated = this.fetchOnCreatedProp;
            }

            if (this.fetchOnUpdateParamsProp !== undefined) {
                data.fetchOnUpdateParams = this.fetchOnUpdateParamsProp;
            }

            if (this.fetchOnUpdatePostDataProp !== undefined) {
                data.fetchOnUpdatePostData = this.fetchOnUpdatePostDataProp;
            }

            return data;
        },
        created: function() {
            created && created.call(this);
            if (this.fetchOnCreated) {
                this.fetch();
            }
        },
        watch: {
            params:{
                handler: function() {
                    if (this.fetchOnUpdateParams) {
                        this.debounceFetch();
                    }
                },
                deep: true
            },
            postData: {
                handler: function() {
                    if (this.fetchOnUpdatePostData) {
                        this.debounceFetch();
                    }
                },
                deep: true
            },
            ...watch
        },
        methods: {
            debounceFetch: debounce(function(){ this.fetch() }, debounceWait),
            fetch: function(successFunc, options) {
                const _this = this;

                if (this.url || this.fetchOptions) {
                    return axios({
                        url: this.url,
                        method: this.method,
                        params: this.params,
                        data: this.postData,
                        ...this.fetchOptions,
                        ...options
                    }).then(function (response) {
                        if (!successFunc || successFunc(response)){
                            _this.data = response.data;
                        }
                    });
                }
            },
            ...methods
        },
        ...options
    }
};

export default fetcher;
